import {
  CodeExecutionContent,
  CodeExecutionResultContent,
  MemoryChunk,
  ToolResultContent,
} from '@frontend/types/specialized-agent';

// Helper types for memory chunk processing
export type ToolUseChunk = MemoryChunk & {
  type: 'tool_use';
  content: {
    execution_id: string;
    name: string;
    input: Record<string, unknown>;
  };
};

export type ToolResultChunk = MemoryChunk & {
  type: 'tool_result';
  content: ToolResultContent;
};

export type TransformedChunk = {
  id: string;
  content: string;
  inputContent?: string;
  outputContent?: string;
  type: string;
  toolName?: string;
  outcome?: string;
};

/**
 * Formats tool call data for display
 */
export function formatToolCall(
  use?: ToolUseChunk,
  result?: ToolResultChunk
): { inputContent: string; outputContent: string; toolName: string } {
  const input = use?.content.input ?? {};
  const output = result?.content.content ?? {};

  const toolName = use?.content.name || result?.content.function_name || 'Unknown Tool';

  return {
    inputContent: '```json\n' + JSON.stringify(input, null, 2) + '\n```',
    outputContent: '```json\n' + JSON.stringify(output, null, 2) + '\n```',
    toolName,
  };
}

/**
 * Creates a map of tool executions to their use/result pairs
 */
export function createToolMap(
  chunks: MemoryChunk[]
): Record<string, { use?: ToolUseChunk; result?: ToolResultChunk }> {
  const toolMap: Record<string, { use?: ToolUseChunk; result?: ToolResultChunk }> = {};

  chunks.forEach(chunk => {
    if (chunk.type === 'tool_use') {
      const execId = (chunk.content as { execution_id: string }).execution_id;
      if (!toolMap[execId]) toolMap[execId] = {};
      toolMap[execId].use = chunk as ToolUseChunk;
    } else if (chunk.type === 'tool_result') {
      const execId = (chunk.content as ToolResultContent).execution_id;
      if (!toolMap[execId]) toolMap[execId] = {};
      toolMap[execId].result = chunk as ToolResultChunk;
    }
  });

  return toolMap;
}

/**
 * Transforms memory chunks into displayable format
 * Used by both AgentExecutionForm and ExecutionDetailsSidebar
 */
export function transformChunks(chunks: MemoryChunk[]): TransformedChunk[] {
  // Sort chunks by sequence number
  const sortedChunks = [...chunks].sort((a, b) => a.sequence_number - b.sequence_number);

  // Create tool map for merging tool_use and tool_result
  const toolMap = createToolMap(chunks);

  // Track processed tool results to avoid showing them separately
  const processedToolResults = new Set<string>();

  return sortedChunks
    .map(chunk => {
      // Handle user messages
      if (chunk.type === 'user_message') {
        return {
          id: chunk.id,
          content: (chunk.content as { text: string }).text ?? '',
          type: 'User',
        };
      }

      // Handle assistant text responses
      if (chunk.type === 'text' && chunk.role === 'assistant') {
        return {
          id: chunk.id,
          content: (chunk.content as { text: string }).text ?? '',
          type: 'Assistant',
        };
      }

      // Handle thinking chunks
      if (chunk.type === 'thinking') {
        return {
          id: chunk.id,
          content: (chunk.content as { thinking: string }).thinking ?? '',
          type: 'Thinking',
        };
      }

      // Handle code execution chunks
      if (chunk.type === 'code_execution') {
        const codeContent = chunk.content as CodeExecutionContent;
        const code = codeContent.code || '';
        return {
          id: chunk.id,
          content: code,
          type: 'Code Execution',
          inputContent: '```' + (codeContent.language || 'python') + '\n' + code + '\n```',
        };
      }

      // Handle code execution result chunks
      if (chunk.type === 'code_execution_result') {
        const resultContent = chunk.content as CodeExecutionResultContent;
        const result = resultContent.result || '';
        return {
          id: chunk.id,
          content: result,
          type: 'Code Result',
          outputContent: result,
          outcome: resultContent.outcome || 'unknown',
        };
      }

      // Handle tool_use - merge with its corresponding tool_result
      if (chunk.type === 'tool_use') {
        const execId = (chunk.content as { execution_id: string }).execution_id;
        const toolPair = toolMap[execId];

        if (toolPair && toolPair.use && toolPair.result) {
          // Mark this tool result as processed so we don't show it again
          processedToolResults.add(execId);

          const toolCall = formatToolCall(toolPair.use, toolPair.result);
          return {
            id: chunk.id,
            content: '', // Empty content since we're using separate input/output
            inputContent: toolCall.inputContent,
            outputContent: toolCall.outputContent,
            type: 'TOOL call',
            toolName: toolCall.toolName,
          };
        }
      }

      return null;
    })
    .filter(Boolean) as TransformedChunk[];
}

/**
 * Alternative transform function used in ExecutionDetailsSidebar
 * Handles tool calls slightly differently for execution history display
 */
export function transformChunksForExecution(chunks: MemoryChunk[]): TransformedChunk[] {
  // Sort chunks by sequence number
  const sortedChunks = [...chunks].sort((a, b) => a.sequence_number - b.sequence_number);

  const toolMap = createToolMap(sortedChunks);
  const result: TransformedChunk[] = [];
  const processedToolCalls = new Set<string>();

  sortedChunks.forEach(chunk => {
    if (chunk.type === 'tool_use' || chunk.type === 'tool_result') {
      const execId =
        chunk.type === 'tool_use'
          ? (chunk.content as { execution_id: string }).execution_id
          : (chunk.content as ToolResultContent).execution_id;

      if (!processedToolCalls.has(execId)) {
        processedToolCalls.add(execId);
        const toolPair = toolMap[execId];
        if (toolPair && (toolPair.use || toolPair.result)) {
          const formatted = formatToolCall(toolPair.use, toolPair.result);
          result.push({
            id: chunk.id,
            content: '',
            inputContent: formatted.inputContent,
            outputContent: formatted.outputContent,
            type: 'TOOL call',
            toolName: formatted.toolName,
          });
        }
      }
    } else if (chunk.type === 'text') {
      result.push({
        id: chunk.id,
        content: (chunk.content as { text: string }).text,
        type: chunk.type,
      });
    } else if (chunk.type === 'user_message') {
      result.push({
        id: chunk.id,
        content: (chunk.content as { text: string }).text,
        type: chunk.type,
      });
    } else if (chunk.type === 'thinking') {
      result.push({
        id: chunk.id,
        content: (chunk.content as { thinking: string }).thinking,
        type: 'Thinking',
      });
    } else if (chunk.type === 'code_execution') {
      const codeContent = chunk.content as CodeExecutionContent;
      const code = codeContent.code || '';
      result.push({
        id: chunk.id,
        content: code,
        type: 'Code Execution',
        inputContent: '```' + (codeContent.language || 'python') + '\n' + code + '\n```',
      });
    } else if (chunk.type === 'code_execution_result') {
      const resultContent = chunk.content as CodeExecutionResultContent;
      const resultText = resultContent.result || '';
      result.push({
        id: chunk.id,
        content: resultText,
        type: 'Code Result',
        outputContent: resultText,
        outcome: resultContent.outcome || 'unknown',
      });
    }
  });

  return result;
}
